import type { GridFilterModel, GridSortModel } from '@leap4/frontend';

import type { ICalculatedMetric, IReport, IReportingResult } from '../report.interface';
import type { IPaginationModel } from '../reportContext';
import { gridFilterModelToFilters, quickSearchFromModel, sortModelToParam } from '../grid/gridQuery.adapters';
import { metricColumnName } from '../grid/reportColumns';
import {
    ADVERTISER_EVENT_METRICS,
    eventNameFromMetricKey,
    eventNameFromPredictedMetricKey,
    isEventMetricKey,
    isPredictedMetricKey,
    PREDICTION_ANCHOR_METRIC,
} from '../reports';

export interface ILiveQueryState {
    sortModel: GridSortModel;
    gridFilterModel: GridFilterModel;
    reportTotals: IReportingResult | null;
    campaignCalculatedMetrics: ICalculatedMetric[];
}

export const buildQueryParams = (
    reportData: IReport,
    pagination: IPaginationModel,
    live: ILiveQueryState,
    useCursor: boolean = true,
    applySort: boolean = true,
): URLSearchParams => {
    const params = new URLSearchParams();

    params.append('startDate', reportData.startDate || '');
    params.append('endDate', reportData.endDate || '');
    params.append('pageSize', String(pagination.pageSize));

    if (useCursor && pagination.cursor) {
        params.append('cursor', pagination.cursor);
    } else {
        params.append('page', String(pagination.page));
    }

    if (live.reportTotals !== null) {
        params.append('includeTotals', 'false');
    }

    const selectedEventMetrics = reportData.metrics
        .map(m => m.eventType)
        .filter(isEventMetricKey)
        .map(eventNameFromMetricKey)
        .filter(Boolean);
    const attributedEvents = Array.from(new Set(selectedEventMetrics));

    const calcDefs = live.campaignCalculatedMetrics;
    const newCalcKeySet = new Set(
        calcDefs.filter((c) => c.target_mode === 'new').map((c) => c.key),
    );

    const selectedCoreMetrics = reportData.metrics
        .map(m => m.eventType)
        .filter(metric => !isEventMetricKey(metric)
            && !newCalcKeySet.has(metric)
            && !isPredictedMetricKey(metric));

    // Predicted metrics are served by a separate read, not as query columns. One metric per event, so
    // the events to predict are named by the selection itself rather than inherited from it.
    const predictedEvents = Array.from(
        new Set(
            reportData.metrics
                .map(m => m.eventType)
                .filter(isPredictedMetricKey)
                .map(eventNameFromPredictedMetricKey)
                .filter(Boolean),
        ),
    );

    const hasAdvertiser = reportData.reportType === 'advertiser'
        || selectedCoreMetrics.some(m => ADVERTISER_EVENT_METRICS.has(m));
    const dateDimKey = hasAdvertiser ? 'event_date' : 'pixel_date';

    // Only the advertiser family still folds the foreign date onto its own axis.
    const userSelectedDimensions = reportData.dimensions.map(
        d => (hasAdvertiser && d.key === 'pixel_date' ? 'event_date' : d.key),
    );

    const shouldAutoAddDate = reportData.timeAggregation
        && reportData.timeAggregation !== 'Sum'
        && !userSelectedDimensions.includes(dateDimKey);

    const finalDimensions = shouldAutoAddDate
        ? [dateDimKey, ...userSelectedDimensions]
        : userSelectedDimensions;

    if (finalDimensions.length > 0) {
        params.append('dimensions', finalDimensions.join(','));
    }

    const outputMetrics = [...selectedCoreMetrics];
    // Anchored here rather than left to the backend's "no metrics at all" default, which fires for a
    // report of nothing but predictions and so put a column on screen nobody asked for and nobody
    // could remove. Explicit means it can be seen, and once anything else is selected it goes away.
    if (
        outputMetrics.length === 0
        && attributedEvents.length === 0
        && !reportData.metrics.some(m => newCalcKeySet.has(m.eventType))
        && predictedEvents.length > 0
    ) {
        outputMetrics.push(PREDICTION_ANCHOR_METRIC);
    }
    if (outputMetrics.length > 0) {
        params.append('outputMetrics', outputMetrics.join(','));
    }
    if (attributedEvents.length > 0) {
        params.append('attributedEvents', attributedEvents.join(','));
    }
    if (predictedEvents.length > 0) {
        params.append('predictedEvents', predictedEvents.join(','));
    }
    const selectedCalcKeys = reportData.metrics
        .map(m => m.eventType)
        .filter(key => newCalcKeySet.has(key));
    if (selectedCalcKeys.length > 0 && reportData.idCampaign) {
        params.append('calculatedMetrics', selectedCalcKeys.join(','));
    }
    params.append('metricFamily', hasAdvertiser ? 'advertiser' : 'pixel');

    reportData.filters.forEach(filter => {
        if (filter.dimension && filter.specTarget.length > 0) {
            const filterValues = filter.specTarget.map(t => t.value).join(',');
            params.append(`filter_${filter.dimension}`, `${filter.action}:${filterValues}`);
        }
    });

    // Drop half-filled rows. A blank value would send NaN, and a constraint on a metric
    // no longer selected is a 400 the user cannot see the cause of. Predicted metrics are excluded
    // for the same reason: they are merged in after the query, so no SQL expression exists to
    // constrain and the backend rejects the whole report.
    const selectedMetricColumns = new Set(
        reportData.metrics
            .filter(m => !isPredictedMetricKey(m.eventType))
            .map(m => metricColumnName(m.eventType)),
    );
    (reportData.constraints || []).forEach(constraint => {
        if (!constraint.metric || constraint.value === '') return;
        const value = Number(constraint.value);
        if (!Number.isFinite(value)) return;
        if (!selectedMetricColumns.has(constraint.metric)) return;
        params.append(
            `constraint_${constraint.metric}`,
            `${constraint.operator}:${value}`,
        );
    });

    for (const gridFilter of gridFilterModelToFilters(live.gridFilterModel)) {
        const csv = gridFilter.values.join(',');
        params.append(
            `filter_${gridFilter.dimension}`,
            csv ? `${gridFilter.operator}:${csv}` : gridFilter.operator,
        );
    }

    if (applySort) {
        const sortParam = sortModelToParam(live.sortModel);
        if (sortParam) {
            params.append('sort', sortParam.field);
            params.append('order', sortParam.direction);
        }
    }
    const search = quickSearchFromModel(live.gridFilterModel);
    if (search) {
        params.append('search', search);
    }

    const userSelectedDate = reportData.dimensions.some(d => d.key === 'pixel_date' || d.key === 'event_date');
    if (reportData.timeAggregation && reportData.timeAggregation !== 'Sum') {
        params.append('timeAggregation', reportData.timeAggregation);
    } else if (userSelectedDate && reportData.timeAggregation === 'Sum') {
        params.append('timeAggregation', 'Day');
    }

    if (reportData.idCampaign && reportData.idCampaign !== 'all') {
        params.append('id_campaign', reportData.idCampaign);
    }

    if (reportData.timezone) {
        params.append('timezone', reportData.timezone);
    }

    return params;
};
